/**
 * To reduce Worker memory size, we are not using a barrel file to import the Worker script.
 * import Logger from '_common/services'; -> should be avoid https://vite.dev/guide/performance#avoid-barrel-files
 */
import Logger from '_common/services/Logger';

export const WORKER_NAME = {
  PROOFING_WORKER: 'PROOFING_WORKER',
} as const;

class DODOCWorker implements DODOC_WebWorker.DODOCWorker {
  #worker: Worker;

  constructor(createWorker: DODOC_WebWorker.WorkerDefinition['createWorker']) {
    this.#worker = createWorker();
  }
  onJobFinish<T>(callback: (result: T) => void) {
    this.#worker.onmessage = (e: MessageEvent<{ result: T }>) => {
      callback(e.data.result);
    };
  }

  startJob(workerArguments: DODOC_WebWorker.WorkerObject): void {
    this.#worker.postMessage(workerArguments);
  }

  destroyWorker(): void {
    this.#worker.terminate();
  }
}
class WorkerFactory {
  private workers = new Map<DODOC_WebWorker.WorkerName, DODOCWorker>();

  get listOfWorkers() {
    return this.workers;
  }

  #createWorker(definition: DODOC_WebWorker.WorkerDefinition): DODOCWorker | undefined {
    if (typeof window === 'undefined' || !window.Worker) {
      // Web Workers are not supported in this environment.
      // TODO: Should we fallback to a non-worker implementation?
      Logger.error("Your browser doesn't support web workers.");

      return undefined;
    }
    const worker = new DODOCWorker(definition.createWorker);
    this.workers.set(definition.key, worker);
    return worker;
  }

  get(key: DODOC_WebWorker.WorkerName): DODOCWorker | undefined {
    let worker = this.workers.get(key);
    if (!worker) {
      switch (key) {
        case WORKER_NAME.PROOFING_WORKER:
          worker = this.#createWorker({
            key: WORKER_NAME.PROOFING_WORKER,
            createWorker: () =>
              new Worker(new URL('./WorkerProofing.ts', import.meta.url), { type: 'module' }),
          });
          break;
        default:
          Logger.info(`Worker with key ${key} not found.`);
          return undefined;
      }
    }
    return worker;
  }

  terminateWorker(key: DODOC_WebWorker.WorkerName): void {
    const worker = this.workers.get(key);
    if (!worker) {
      return;
    }

    worker.destroyWorker();
    this.workers.delete(key);
  }
}
export class WorkerManager {
  private static workerManagerInstance: WorkerManager;

  private workerFactory: WorkerFactory;

  private constructor() {
    this.workerFactory = new WorkerFactory();
  }

  static getInstance(): WorkerManager {
    if (!WorkerManager.workerManagerInstance) {
      WorkerManager.workerManagerInstance = new WorkerManager();
    }
    return WorkerManager.workerManagerInstance;
  }

  getWorker(type: DODOC_WebWorker.WorkerName): DODOCWorker | undefined {
    switch (type) {
      case WORKER_NAME.PROOFING_WORKER:
        return this.workerFactory.get(type);
      default:
        return undefined;
    }
  }

  destroy(): void {
    for (const workerKey of this.workerFactory.listOfWorkers.keys()) {
      this.workerFactory.terminateWorker(workerKey);
    }
  }
}
